import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Trash2, Wallet } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Button } from "./ui/button";
import { OpeningEditor } from "./Payments";
import { api, errMsg, money } from "../lib/api";

const EMPTY = { name: "", phone: "", address: "", tax_office: "", tax_no: "" };
const FIELDS = [["name", "Firma Unvanı *"], ["phone", "Telefon"], ["tax_office", "Vergi Dairesi"], ["tax_no", "Vergi No"], ["address", "Adres"]];
const today = () => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`; };
export const fmtDay = (s) => (s ? s.split("-").reverse().join(".") : "-");

export const SupplierFormDialog = ({ open, onOpenChange, supplier, onSaved }) => {
  const [f, setF] = useState(EMPTY);
  useEffect(() => setF(supplier ? { ...EMPTY, ...supplier } : EMPTY), [supplier, open]);
  const save = async (e) => {
    e.preventDefault();
    try {
      const body = Object.fromEntries(FIELDS.map(([k]) => [k, f[k] || ""]));
      const { data } = supplier ? await api.put(`/suppliers/${supplier.id}`, body) : await api.post("/suppliers", body);
      toast.success("Tedarikçi kaydedildi");
      onSaved(data); onOpenChange(false);
    } catch (ex) { toast.error(errMsg(ex)); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="w-[calc(100%-1.5rem)] max-w-lg rounded-xl bg-white" data-testid="supplier-form-dialog">
        <DialogHeader><DialogTitle className="font-display">{supplier ? "Tedarikçiyi Düzenle" : "Yeni Tedarikçi"}</DialogTitle></DialogHeader>
        <form onSubmit={save} className="grid grid-cols-2 gap-3">
          {FIELDS.map(([k, l]) => (
            <div key={k} className={k === "name" || k === "address" ? "col-span-2" : ""}><Label className="text-xs">{l}</Label>
              <Input data-testid={`supplier-form-${k}-input`} required={k === "name"} value={f[k]} onChange={(e) => setF({ ...f, [k]: e.target.value })} className="mt-1" /></div>
          ))}
          <div className="col-span-2 flex justify-end gap-2 pt-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Vazgeç</Button>
            <Button data-testid="supplier-form-save-button" className="bg-slate-950 hover:bg-blue-600">Kaydet</Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

const SupplierPaymentForm = ({ supplier, balance, onDone }) => {
  const [f, setF] = useState({ amount: "", note: "", payment_date: today() });
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    const a = Number(f.amount);
    if (!(a > 0)) return toast.error("Geçerli bir tutar girin");
    setBusy(true);
    try { await api.post(`/suppliers/${supplier.id}/payments`, { ...f, amount: a }); toast.success(`${money(a)} ödeme kaydedildi`); setF({ amount: "", note: "", payment_date: today() }); onDone(); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <div className="space-y-2 rounded-lg border border-emerald-200 bg-emerald-50 p-3">
      <p className="flex items-center gap-2 text-sm font-semibold text-emerald-900"><Wallet className="h-4 w-4" />Tedarikçiye Ödeme Yap</p>
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
        <Input data-testid="supplier-payment-amount-input" type="number" min="0.01" step="0.01" value={f.amount} onChange={(e) => setF({ ...f, amount: e.target.value })} placeholder="Tutar (₺)" className="bg-white" />
        <Input data-testid="supplier-payment-date-input" type="date" value={f.payment_date} onChange={(e) => setF({ ...f, payment_date: e.target.value })} className="bg-white" />
        <Input data-testid="supplier-payment-note-input" value={f.note} onChange={(e) => setF({ ...f, note: e.target.value })} placeholder="Not (havale, çek...)" className="col-span-2 bg-white" />
      </div>
      <div className="flex gap-2">
        {balance > 0 && <Button data-testid="supplier-payment-full-button" type="button" size="sm" variant="outline" onClick={() => setF({ ...f, amount: String(balance) })}>Tüm Borç ({money(balance)})</Button>}
        <Button data-testid="supplier-payment-submit-button" size="sm" disabled={busy} onClick={submit} className="ml-auto bg-emerald-600 hover:bg-emerald-700">Ödemeyi Kaydet</Button>
      </div>
    </div>
  );
};

const Stat = ({ label, value, id, cls = "border-slate-200" }) => (
  <div className={`rounded-xl border p-3 ${cls}`}>
    <p className="font-mono text-[10px] uppercase tracking-wider opacity-70">{label}</p>
    <p className="mt-1 font-display text-lg font-bold" data-testid={id}>{value}</p>
  </div>
);

export const SupplierLedgerDialog = ({ supplier, onClose, onChanged }) => {
  const [data, setData] = useState(null);
  const load = () => api.get(`/suppliers/${supplier.id}/ledger`).then((r) => setData(r.data)).catch((e) => toast.error(errMsg(e)));
  useEffect(() => { setData(null); if (supplier) load(); }, [supplier]); // eslint-disable-line
  const refresh = () => { load(); onChanged(); };
  const delPay = async (p) => {
    if (!window.confirm(`${money(p.amount)} ödeme silinsin mi?`)) return;
    try { await api.delete(`/suppliers/${supplier.id}/payments/${p.id}`); toast.success("Ödeme silindi"); refresh(); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <Dialog open={!!supplier} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="w-[calc(100%-1.5rem)] max-w-3xl rounded-xl bg-white max-h-[90vh] overflow-y-auto" data-testid="supplier-ledger-dialog">
        {supplier && <>
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">{supplier.name}</DialogTitle>
            <p className="text-sm text-slate-500">{[supplier.phone, supplier.tax_office, supplier.tax_no].filter(Boolean).join(" · ")}</p>
          </DialogHeader>
          {!data ? <p className="py-8 text-center text-sm text-slate-500">Yükleniyor...</p> : <>
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
              <Stat label="Devir" value={money(data.summary.opening)} id="supplier-summary-opening" />
              <Stat label="Alışlar" value={money(data.summary.purchases_total)} id="supplier-summary-purchases" />
              <Stat label="Ödenen" value={money(data.summary.paid)} id="supplier-summary-paid" cls="border-emerald-200 bg-emerald-50 text-emerald-900" />
              <Stat label="Borcumuz" value={money(data.summary.balance)} id="supplier-summary-balance" cls="border-slate-950 bg-slate-950 text-white" />
            </div>
            <OpeningEditor testId="supplier-opening" url={`/suppliers/${supplier.id}/opening`} value={data.summary.opening} onSaved={refresh} />
            <SupplierPaymentForm supplier={supplier} balance={data.summary.balance} onDone={refresh} />
            <div>
              <h3 className="mb-2 font-display font-semibold">Alış Faturaları</h3>
              <div className="divide-y divide-slate-100 rounded-lg border border-slate-200">
                {data.purchases.length === 0 && <p className="p-4 text-center text-sm text-slate-500">Alış faturası yok</p>}
                {data.purchases.map((p) => (
                  <div key={p.id} className="flex items-center justify-between gap-2 p-3 text-sm" data-testid={`supplier-purchase-${p.purchase_no}`}>
                    <div><p className="font-mono font-semibold">{p.purchase_no} {p.invoice_no && <span className="font-sans font-normal text-slate-500">· Fatura {p.invoice_no}</span>}</p>
                      <p className="text-xs text-slate-500">{fmtDay(p.invoice_date)} · {p.items.length} kalem</p></div>
                    <span className="font-display font-bold">{money(p.grand_total)}</span>
                  </div>
                ))}
              </div>
            </div>
            <div>
              <h3 className="mb-2 font-display font-semibold">Ödemeler</h3>
              <div className="divide-y divide-slate-100 rounded-lg border border-slate-200">
                {data.payments.length === 0 && <p className="p-4 text-center text-sm text-slate-500">Ödeme yok</p>}
                {data.payments.map((p) => (
                  <div key={p.id} className="flex items-center justify-between gap-2 p-3 text-sm" data-testid={`supplier-payment-${p.id}`}>
                    <div><p className="font-semibold text-emerald-700">{money(p.amount)}</p><p className="text-xs text-slate-500">{fmtDay(p.payment_date)} {p.note && `· ${p.note}`}</p></div>
                    <button data-testid={`supplier-payment-delete-${p.id}`} onClick={() => delPay(p)} className="text-slate-400 hover:text-rose-600"><Trash2 className="h-4 w-4" /></button>
                  </div>
                ))}
              </div>
            </div>
          </>}
        </>}
      </DialogContent>
    </Dialog>
  );
};
